import { isEnvironmentalSensor } from '@/Components/Common/deviceUtils';
import {
  convertTemperature,
  determineTempTrendFromValues,
} from '@/Components/Environmental/EnvironmentalUtils';
import {
  COOKTOP_CATEGORY,
  DEVICE_STATE_PROPERTY,
  EVENT_PROPERTIES,
  EVENT_TYPES,
  RADAR_PRESENCE_TYPES,
  SENSOR_BATTERY_THRESHOLDS,
  SENSOR_PROPERTY,
  SENSORS,
  SENSOR_VARIANTS,
} from '@/constants/device';
import {
  formatEnergy,
  formatEnergyUnit,
  getBatteryLevelClassName,
  getDatelabel,
  getStatusClassNames,
  getStatusClassNamesFromSseEvent,
  getSensorVariant,
} from '@/utilities/helpers/commonHelper';
import { getTimeFormat, getTimeZoneFormat } from '@vantiva-sa/homesight_humaui/Utilities/DateTime';
import { get, groupBy, has, isArray, isEmpty, isNil, isNumber, last, toNumber, trim } from 'lodash';
import moment from 'moment-timezone';

const getLatestData = (sensorEvents) => {
  const events = [...sensorEvents].sort(
    (a, b) => new Date(b.utc_timestamp_inms) - new Date(a.utc_timestamp_inms)
  );
  return events[0] || {};
};

/**
 * Returns a formatted state or value for a given sensor event, depending on sensor type and property.
 *
 * @param {Object} eventData - The event data object containing sensor readings and state.
 * @param {Object} sensor - The sensor object, must include at least a `devicetype` property.
 * @param {string} property - The property to extract or compute (e.g., 'temperature', 'humidity', 'powerStatus', etc.).
 * @param {function} trans - Translation function for status strings (e.g., (key: string) => string).
 * @param {string} principleTempUnit - The preferred temperature unit ('C' or 'F').
 * @returns {string} The formatted sensor state or value. Returns '-' if data is unavailable or property is not applicable.
 *
 * @example
 * // For a temperature sensor:
 * getSensorState(eventData, sensor, 'temperature', trans, 'C'); // "22°C"
 *
 * // For a door/window sensor:
 * getSensorState(eventData, sensor, 'status', trans, 'C'); // "OPEN" or "CLOSED"
 *
 * // For a smart plug:
 * getSensorState(eventData, sensor, 'powerStatus', trans, 'C'); // "ON" or "OFF"
 *
 * // For environmental sensor with trend:
 * getSensorState(eventData, sensor, 'tempTrend', trans, 'C'); // "rising", "falling", etc.
 *
 * // Returns '-' if the value is not available or not applicable.
 */
const getSensorState = (eventData, sensor, property, trans, tempUnit) => {
  if (!eventData) return '-';

  const type = sensor.devicetype;

  // Special-cases first
  if (type === SENSORS.SMART_HOME.COOKTOP_SENSOR) {
    const isPresenceEvent = eventData?.type?.startsWith(EVENT_TYPES.COOKTOP_SENSOR.PRESENCE + '.');
    const isPresent =
      isPresenceEvent &&
      (eventData?.iseventdetected === true ||
        (eventData?.presence_status || '').toLowerCase() === 'detected');
    return isPresent ? 'ACTIVE' : 'INACTIVE';
  }

  // Environmental aggregated fields from observations
  if (type === SENSORS.SMART_HOME.ENVIRONMENTAL_SENSOR) {
    const obsKeyByProp = {
      temperature: SENSOR_PROPERTY.ENVIRONMENTAL_SENSOR.TEMPERATURE,
      humidity: SENSOR_PROPERTY.ENVIRONMENTAL_SENSOR.HUMIDITY,
      ambientLight: SENSOR_PROPERTY.ENVIRONMENTAL_SENSOR.AMBIENT_LIGHT,
    };
    const obsKey = obsKeyByProp[property];
    if (obsKey) {
      const observations = isArray(eventData?.[obsKey])
        ? eventData?.[obsKey]
        : eventData?.[obsKey]?.split(',');
      let unit = '';
      if (property === 'temperature') {
        unit = `°${tempUnit}`;
      }
      if (property === 'humidity') {
        unit = '%';
      }

      const lastObservation = last(observations);

      if (property === 'temperature') {
        let currentUnit = eventData[DEVICE_STATE_PROPERTY.temperature_sensor_unit];
        if (!currentUnit) {
          currentUnit = eventData[DEVICE_STATE_PROPERTY.temperature_sensor_unit_2];
        }
        const value = convertTemperature(Number(lastObservation), currentUnit, tempUnit);
        return `${Math.round(value)}${unit}`;
      }

      return `${Math.round(toNumber(last(observations)))}${unit}`;
    }
    if (property === 'tempTrend') {
      const observations =
        eventData?.[SENSOR_PROPERTY.ENVIRONMENTAL_SENSOR.TEMPERATURE]?.split(',');
      return determineTempTrendFromValues(observations);
    }
  }

  // Resolve state property for all other types
  const variant = getSensorVariant(sensor);
  let baseStateProperty = DEVICE_STATE_PROPERTY[type];

  // Override state property for incontinence sensor variant
  if (type === SENSORS.SMART_HOME.OCCUPANCY_SENSOR && variant === SENSOR_VARIANTS.INCONTINENCE) {
    baseStateProperty = DEVICE_STATE_PROPERTY.occupancy_sensor_incontinence;
  }

  const statePropertyOverrides = {
    // smart plug power/energy
    powerUsage: DEVICE_STATE_PROPERTY.smart_plug_lan,
    powerUnit: DEVICE_STATE_PROPERTY.smart_plug_lan,
    // temperature/humidity sensor
    ...((type === SENSORS.SMART_HOME.TEMPERATURE_HUMIDITY_SENSOR ||
      type === SENSORS.SMART_HOME.OUTDOOR_TEMPERATURE_HUMIDITY_SENSOR) && {
      temperature: DEVICE_STATE_PROPERTY.temperature_sensor,
      humidity: DEVICE_STATE_PROPERTY.temperature_sensor_humidity,
    }),
  };
  const stateProperty = statePropertyOverrides[property] || baseStateProperty;

  const stateVal = eventData[stateProperty];
  if (isNil(stateVal)) return '-';

  // Boolean -> translated status
  const boolToText = (onText, offText) =>
    stateVal === true ? onText : stateVal === false ? offText : '-';

  if (type === SENSORS.SMART_HOME.DOOR_WINDOW_SENSOR) {
    return boolToText('OPEN', 'CLOSED');
  }

  if (type === SENSORS.SMART_HOME.OCCUPANCY_SENSOR) {
    if (variant === SENSOR_VARIANTS.INCONTINENCE) {
      return boolToText('WET', 'DRY');
    }
    return boolToText('OCCUPIED', 'VACANT');
  }

  if (type === SENSORS.SMART_HOME.RADAR_SENSOR) {
    return stateVal ? String(stateVal).toUpperCase() : '-';
  }

  if (type === SENSORS.SMART_HOME.SMART_PLUG) {
    if (property === 'powerStatus') return boolToText('ON', 'OFF');
    if (property === 'powerUsage') return formatEnergy(stateVal);
    if (property === 'powerUnit') return formatEnergyUnit(stateVal);
  }

  // Numeric conversions and formatting
  const isTempContext =
    type === SENSORS.SMART_HOME.TEMPERATURE_HUMIDITY_SENSOR ||
    type === SENSORS.SMART_HOME.ENVIRONMENTAL_SENSOR ||
    type === SENSORS.SMART_HOME.OUTDOOR_TEMPERATURE_HUMIDITY_SENSOR;

  if (isTempContext) {
    if (property === 'temperature') {
      const currentUnit = eventData[DEVICE_STATE_PROPERTY.temperature_sensor_unit];
      const value = convertTemperature(stateVal, currentUnit, tempUnit);
      return `${Math.round(value)}°${tempUnit}`;
    }
    if (property === 'humidity') {
      return `${stateVal}%`;
    }
  }

  return '-';
};

export const updateSensorStateFromSSE = (
  currentSensorData,
  sseEvent,
  sensor,
  trans,
  tempUnit,
  timezone,
  loginUserTimeformat
) => {
  if (
    !isEmpty(sseEvent) &&
    currentSensorData.deviceuuid === get(sseEvent, EVENT_PROPERTIES.ENTITY_UUID, '')
  ) {
    const messageType = get(sseEvent, EVENT_PROPERTIES.MESSAGE_TYPE, '');
    let timestamp = get(sseEvent, EVENT_PROPERTIES.TIMESTAMP, '');
    if (isNumber(timestamp) && timestamp > 0) {
      timestamp = new Date(timestamp).toISOString();
    }

    currentSensorData.time = !isEmpty(timestamp)
      ? getTimeFormat(timestamp, getTimeZoneFormat(timezone), loginUserTimeformat?.is24hoursformat)
      : '-';

    if (
      [
        EVENT_TYPES.OCCUPANCY_SENSOR.ACCESSORY_STATE_OCCUPANCY_SENSOR_ACTIVE,
        EVENT_TYPES.OCCUPANCY_SENSOR.ACCESSORY_STATE_UPDATE_OCCUPANCY_SENSOR_WET,
        EVENT_TYPES.DOOR_WINDOW_SENSOR.ACCESSORY_STATE_UPDATE_DOOR_WINDOW_SENSOR_OPEN,
      ].includes(messageType)
    ) {
      currentSensorData.sensorState = getSensorState(sseEvent, sensor, null, trans, null);
    }

    if (messageType === EVENT_TYPES.RADAR_SENSOR.RADAR_PRESENCE_EVENT) {
      currentSensorData.sensorState = getSensorState(sseEvent, sensor, null, trans, null);
      currentSensorData.zoneName = get(sseEvent, `${messageType}__zone_name`, '-');

      // Update fall state fields dynamically from the incoming SSE event
      const presenceType = String(
        get(sseEvent, `${messageType}__presence_type`, '') || ''
      ).toLowerCase();

      if (
        presenceType === RADAR_PRESENCE_TYPES.FALL_DETECTED ||
        presenceType === RADAR_PRESENCE_TYPES.FALL_RECOVERED
      ) {
        currentSensorData.currentFallState = presenceType;
        currentSensorData.fallStatus = presenceType;
        currentSensorData.fallEventTime = !isEmpty(timestamp)
          ? getTimeFormat(
              timestamp,
              getTimeZoneFormat(timezone),
              loginUserTimeformat?.is24hoursformat
            )
          : '-';
        // Increment the fall count when a new fall_detected event arrives
        if (presenceType === RADAR_PRESENCE_TYPES.FALL_DETECTED) {
          currentSensorData.fallsTodayCount = (currentSensorData.fallsTodayCount || 0) + 1;
        }
      }
    }

    if (messageType === EVENT_TYPES.COOKTOP_SENSOR.DETECTION) {
      const eventType = get(sseEvent, 'type', '');
      const isDetected = get(sseEvent, 'iseventdetected', false) === true;
      const alertPrefixes = [
        EVENT_TYPES.COOKTOP_SENSOR.EXPOSED_BURNER,
        EVENT_TYPES.COOKTOP_SENSOR.POT_TOO_HOT,
        EVENT_TYPES.COOKTOP_SENSOR.UNATTENDED,
      ];
      if (eventType?.startsWith(EVENT_TYPES.COOKTOP_SENSOR.PRESENCE + '.')) {
        const isPresenceDetected =
          isDetected || (get(sseEvent, 'presence_status', '') || '').toLowerCase() === 'detected';
        currentSensorData.sensorState = isPresenceDetected ? 'ACTIVE' : 'INACTIVE';
      }
      if (eventType?.startsWith(EVENT_TYPES.COOKTOP_SENSOR.ACTIVE_COOKING + '.')) {
        currentSensorData.cookingSessionActive = isDetected;
        if (isDetected) {
          currentSensorData.cookingLastSessionTime = !isEmpty(timestamp)
            ? getTimeFormat(
                timestamp,
                getTimeZoneFormat(timezone),
                loginUserTimeformat?.is24hoursformat
              )
            : '-';
        } else {
          currentSensorData.cookingLastSessionTime = !isEmpty(timestamp)
            ? getTimeFormat(
                timestamp,
                getTimeZoneFormat(timezone),
                loginUserTimeformat?.is24hoursformat
              )
            : currentSensorData.cookingLastSessionTime;
        }
      }
      const matchedAlertPrefix = alertPrefixes.find((p) => eventType?.startsWith(p + '.'));
      if (matchedAlertPrefix) {
        const rawKey = matchedAlertPrefix.split('.').pop().toUpperCase();
        const alertKey = rawKey === 'UNATTENDED' ? 'UNATTENDED_COOKING' : rawKey;
        currentSensorData.alerts = isDetected ? alertKey : null;
      }
    }

    if (messageType === EVENT_TYPES.SMART_PLUG.ACCESSORY_STATE_UPDATE_SMART_PLUG_ON) {
      currentSensorData.powerStatus = getSensorState(sseEvent, sensor, 'powerStatus', trans, null);
    }

    if (messageType === EVENT_TYPES.SMART_PLUG.ACCESSORY_STATE_LAN_SMART_PLUG) {
      currentSensorData.powerUsage = getSensorState(sseEvent, sensor, 'powerUsage', trans, null);
      currentSensorData.powerUnit = getSensorState(sseEvent, sensor, 'powerUnit', trans, null);
    }

    if (
      [
        EVENT_TYPES.ENVIRONMENTAL_SENSOR.SENSOR_TEMPERATURE_REPORT,
        EVENT_TYPES.TEMPERATURE_HUMIDITY_SENSOR.ACCESSORY_STATE_BLE_TEMPERATURE_SENSOR,
      ].includes(messageType)
    ) {
      currentSensorData.temperature = getSensorState(
        sseEvent,
        sensor,
        'temperature',
        trans,
        tempUnit
      );
    }

    if (
      [
        EVENT_TYPES.ENVIRONMENTAL_SENSOR.SENSOR_HUMIDITY_REPORT,
        EVENT_TYPES.TEMPERATURE_HUMIDITY_SENSOR.ACCESSORY_STATE_BLE_TEMPERATURE_SENSOR,
      ].includes(messageType)
    ) {
      currentSensorData.humidity = getSensorState(sseEvent, sensor, 'humidity', trans, null);
    }

    if (messageType === EVENT_TYPES.ENVIRONMENTAL_SENSOR.SENSOR_LIGHT_REPORT) {
      currentSensorData.ambientLight = getSensorState(
        sseEvent,
        sensor,
        'ambientLight',
        trans,
        null
      );
    }

    const connectedProp = `${messageType}__${EVENT_PROPERTIES.CONNECTED}`;
    if (has(sseEvent, connectedProp) && !isNil(sseEvent[connectedProp])) {
      currentSensorData.sensorStatus = trim(
        getStatusClassNamesFromSseEvent(sseEvent, connectedProp)
      );
    }

    const batteryLevelProp = `${messageType}__${EVENT_PROPERTIES.BATTERY_LEVEL}`;
    if (has(sseEvent, batteryLevelProp) && !isNil(sseEvent[batteryLevelProp])) {
      currentSensorData.batteryStatus = trim(
        getBatteryLevelClassName({
          deviceBatteryLevel: sseEvent[batteryLevelProp],
          deviceSetting: SENSOR_BATTERY_THRESHOLDS,
        })
      );
      currentSensorData.batteryLevel = sseEvent[batteryLevelProp];
    }
  }

  return currentSensorData;
};

export const sensorCardAdapter = ({
  sensor,
  sensorLatestEvents,
  timezone,
  loginUserTimeformat,
  loginUserLocale,
  trans,
  tempUnit,
  aggregatedEvents,
  envEventTypes,
  radarTodayEvents,
  cooktopTodayEvents,
}) => {
  const groupedLatestEvent = {};
  if (sensorLatestEvents.length > 2) {
    const groupedEvents = groupBy(sensorLatestEvents, 'message_type');
    Object.values(groupedEvents).forEach((events) => {
      const latestEvent = getLatestData(events);
      groupedLatestEvent[latestEvent.message_type] = latestEvent;
    });
  }
  let lastEventData = getLatestData(sensorLatestEvents);
  const radarEvents = sensorLatestEvents.filter(
    (event) => event?.message_type === EVENT_TYPES.RADAR_SENSOR.RADAR_PRESENCE_EVENT
  );

  if (sensor.devicetype === SENSORS.SMART_HOME.OUTDOOR_TEMPERATURE_HUMIDITY_SENSOR) {
    lastEventData = sensor; // we dont have events for outdoor sensor, so using sensor object itself
  }

  const getSensorStatus = () => {
    if (sensor.devicetype === SENSORS.SMART_HOME.ENVIRONMENTAL_SENSOR) {
      return trim(getStatusClassNames(sensor, 'device_connected'));
    }
    return trim(getStatusClassNames(sensor));
  };

  const getTemperature = () => {
    if (
      sensor.devicetype === SENSORS.SMART_HOME.TEMPERATURE_HUMIDITY_SENSOR ||
      sensor.devicetype === SENSORS.SMART_HOME.OUTDOOR_TEMPERATURE_HUMIDITY_SENSOR
    ) {
      return getSensorState(lastEventData, sensor, 'temperature', trans, tempUnit);
    }
    if (sensor.devicetype === SENSORS.SMART_HOME.ENVIRONMENTAL_SENSOR) {
      return getSensorState(
        groupedLatestEvent[EVENT_TYPES.ENVIRONMENTAL_SENSOR.SENSOR_TEMPERATURE_REPORT],
        sensor,
        'temperature',
        trans,
        tempUnit
      );
    }
    return '-';
  };

  const getHumidity = () => {
    if (
      sensor.devicetype === SENSORS.SMART_HOME.TEMPERATURE_HUMIDITY_SENSOR ||
      sensor.devicetype === SENSORS.SMART_HOME.OUTDOOR_TEMPERATURE_HUMIDITY_SENSOR
    ) {
      return getSensorState(lastEventData, sensor, 'humidity', trans, null);
    }
    if (sensor.devicetype === SENSORS.SMART_HOME.ENVIRONMENTAL_SENSOR) {
      return getSensorState(
        groupedLatestEvent[EVENT_TYPES.ENVIRONMENTAL_SENSOR.SENSOR_HUMIDITY_REPORT],
        sensor,
        'humidity',
        trans,
        null
      );
    }
    return '-';
  };

  const getCookingSessionsCount = () => {
    const timezoneName = timezone?.split(' ')[0];
    const todayStart = timezoneName
      ? moment.tz(timezoneName).startOf('day')
      : moment().startOf('day');
    const candidates = [...(cooktopTodayEvents || []), ...sensorLatestEvents];
    const seen = new Set();
    return candidates.filter((e) => {
      if (
        e.type?.startsWith(EVENT_TYPES.COOKTOP_SENSOR.ACTIVE_COOKING + '.') &&
        e.iseventdetected === true &&
        moment(e.utc_timestamp_inms).isSameOrAfter(todayStart)
      ) {
        if (seen.has(e.id ?? e.utc_timestamp_inms)) return false;
        seen.add(e.id ?? e.utc_timestamp_inms);
        return true;
      }
      return false;
    }).length;
  };

  const getFlushedCount = (aggregatedEvents) => {
    if (!aggregatedEvents || !envEventTypes?.length) {
      return 0;
    }
    const aggregated = get(aggregatedEvents, envEventTypes[0], []);
    if (!aggregated.length) {
      return 0;
    }
    const timezoneName = timezone?.split(' ')[0];
    if (!timezoneName) {
      return 0;
    }
    const localDay = moment
      .tz(Date.now(), timezoneName)
      .startOf('day')
      .format('YYYY-MM-DDT00:00:00');
    const matchingAggregation = aggregated.find((entry) => entry?.localTime === localDay);
    const totalFlushes = Number(get(matchingAggregation, ['active', 'true', 'sum'], 0) || 0);
    return totalFlushes;
  };

  const getRadarFallInfo = () => {
    if (sensor.devicetype !== SENSORS.SMART_HOME.RADAR_SENSOR) {
      return { status: '-', eventTime: '-' };
    }

    const timezoneName = timezone?.split(' ')[0];
    if (!timezoneName) {
      return { status: '-', eventTime: '-' };
    }

    // Use today's full event set if available, fall back to latest events
    const eventsToSearch =
      Array.isArray(radarTodayEvents) && radarTodayEvents.length ? radarTodayEvents : radarEvents;

    if (!eventsToSearch.length) {
      return { status: '-', eventTime: '-' };
    }

    const todayStart = moment.tz(Date.now(), timezoneName).startOf('day');
    const todayEnd = moment.tz(Date.now(), timezoneName).endOf('day');

    const latestFallEvent = getLatestData(
      eventsToSearch.filter((event) => {
        // radarTodayEvents uses 'presence_type' directly; sensorLatestEvents uses the flattened key
        const type = String(
          event?.presence_type || event?.radar_presence_event__presence_type || ''
        ).toLowerCase();
        if (
          type !== RADAR_PRESENCE_TYPES.FALL_DETECTED &&
          type !== RADAR_PRESENCE_TYPES.FALL_RECOVERED
        )
          return false;
        const eventTime = moment.tz(event?.utc_timestamp_inms, timezoneName);
        return eventTime.isBetween(todayStart, todayEnd, null, '[]');
      })
    );

    if (isEmpty(latestFallEvent)) {
      return { status: '-', eventTime: '-' };
    }

    const presenceType = String(
      latestFallEvent?.presence_type || latestFallEvent?.radar_presence_event__presence_type || ''
    ).toLowerCase();

    return {
      status: presenceType,
      eventTime: latestFallEvent?.utc_timestamp_inms
        ? getTimeFormat(
            latestFallEvent.utc_timestamp_inms,
            getTimeZoneFormat(timezone),
            loginUserTimeformat?.is24hoursformat
          )
        : '-',
    };
  };

  const getRadarFallsTodayCount = () => {
    if (sensor.devicetype !== SENSORS.SMART_HOME.RADAR_SENSOR) {
      return 0;
    }

    const timezoneName = timezone?.split(' ')[0];
    if (!timezoneName) {
      return 0;
    }

    if (aggregatedEvents && envEventTypes?.length) {
      const aggregated = get(aggregatedEvents, envEventTypes[0], []);
      if (Array.isArray(aggregated) && aggregated.length) {
        const localDay = moment
          .tz(Date.now(), timezoneName)
          .startOf('day')
          .format('YYYY-MM-DDT00:00:00');

        const matchingAggregation = aggregated.find((entry) => entry?.localTime === localDay) || {};

        const aggregatedCount = Number(
          get(
            matchingAggregation,
            ['presence_type', RADAR_PRESENCE_TYPES.FALL_DETECTED, 'sum'],
            get(matchingAggregation, [RADAR_PRESENCE_TYPES.FALL_DETECTED, 'sum'], 0)
          ) || 0
        );

        if (aggregatedCount > 0) {
          return aggregatedCount;
        }
      }
    }

    const todayStart = moment.tz(Date.now(), timezoneName).startOf('day');
    const todayEnd = moment.tz(Date.now(), timezoneName).endOf('day');

    return radarEvents.filter((event) => {
      const presenceType = String(event?.radar_presence_event__presence_type || '').toLowerCase();
      if (presenceType !== RADAR_PRESENCE_TYPES.FALL_DETECTED) return false;
      const eventTime = moment.tz(event?.utc_timestamp_inms, timezoneName);
      return eventTime.isBetween(todayStart, todayEnd, null, '[]');
    }).length;
  };

  const radarFallInfo = getRadarFallInfo();
  const radarFallsTodayCount = getRadarFallsTodayCount();

  const cookingSessionActive =
    sensor.devicetype === SENSORS.SMART_HOME.COOKTOP_SENSOR
      ? (() => {
          const allCookingEvents = [...(cooktopTodayEvents || []), ...sensorLatestEvents].filter(
            (e) => e.type?.startsWith(EVENT_TYPES.COOKTOP_SENSOR.ACTIVE_COOKING + '.')
          );
          const latest = getLatestData(allCookingEvents);
          return isEmpty(latest) ? false : latest.iseventdetected === true;
        })()
      : undefined;

  return {
    deviceuuid: sensor.deviceuuid,
    location: sensor.location || '-',
    modelname: isEnvironmentalSensor(sensor) ? sensor.device_serial_number : sensor.modelname,
    deviceType: sensor.devicetype,
    variant: getSensorVariant(sensor),
    connectionless: get(sensor, 'connectionless', false),
    sensorStatus: getSensorStatus(),
    batteryStatus: trim(
      getBatteryLevelClassName({
        deviceBatteryLevel: sensor?.device_battery_level,
        deviceSetting: SENSOR_BATTERY_THRESHOLDS,
      })
    ),
    batteryLevel: sensor?.device_battery_level,
    date: getDatelabel(lastEventData, timezone, loginUserLocale, trans) || '-',
    time: lastEventData?.utc_timestamp_inms
      ? getTimeFormat(
          lastEventData?.utc_timestamp_inms,
          getTimeZoneFormat(timezone),
          loginUserTimeformat?.is24hoursformat
        )
      : '-',
    sensorState:
      sensor.devicetype === SENSORS.SMART_HOME.COOKTOP_SENSOR
        ? (() => {
            // Prefer the latest presence-specific event if available
            const latestPresence = getLatestData(
              sensorLatestEvents.filter((e) =>
                e.type?.startsWith(EVENT_TYPES.COOKTOP_SENSOR.PRESENCE + '.')
              )
            );
            if (!isEmpty(latestPresence)) {
              return latestPresence.iseventdetected === true ? 'ACTIVE' : 'INACTIVE';
            }
            // Fallback: read presence_status directly from the last event
            if (!lastEventData) return '-';
            const presenceStatus = (lastEventData[COOKTOP_CATEGORY.PRESENCE] || '').toLowerCase();
            return presenceStatus === 'detected' ? 'ACTIVE' : 'INACTIVE';
          })()
        : getSensorState(lastEventData, sensor, null, trans, null), //use this when event types count is 1. if its more, then create 1 key for each event type
    powerStatus: getSensorState(
      groupedLatestEvent[EVENT_TYPES.SMART_PLUG.ACCESSORY_STATE_UPDATE_SMART_PLUG_ON],
      sensor,
      'powerStatus',
      trans,
      null
    ),
    powerUsage: getSensorState(
      groupedLatestEvent[EVENT_TYPES.SMART_PLUG.ACCESSORY_STATE_LAN_SMART_PLUG],
      sensor,
      'powerUsage',
      trans,
      null
    ),
    powerUnit: getSensorState(
      groupedLatestEvent[EVENT_TYPES.SMART_PLUG.ACCESSORY_STATE_LAN_SMART_PLUG],
      sensor,
      'powerUnit',
      trans,
      null
    ),
    temperature: getTemperature(),
    humidity: getHumidity(),
    tempTrend: getSensorState(
      groupedLatestEvent[EVENT_TYPES.ENVIRONMENTAL_SENSOR.SENSOR_TEMPERATURE_REPORT],
      sensor,
      'tempTrend',
      trans,
      null
    ),
    alerts:
      sensor.devicetype === SENSORS.SMART_HOME.COOKTOP_SENSOR
        ? (() => {
            const alertPrefixes = [
              EVENT_TYPES.COOKTOP_SENSOR.EXPOSED_BURNER,
              EVENT_TYPES.COOKTOP_SENSOR.POT_TOO_HOT,
              EVENT_TYPES.COOKTOP_SENSOR.UNATTENDED,
            ];
            const allAlertEvents = [...(cooktopTodayEvents || []), ...sensorLatestEvents].filter(
              (e) => alertPrefixes.some((p) => e.type?.startsWith(p + '.'))
            );
            const latestAlert = getLatestData(allAlertEvents);
            if (isEmpty(latestAlert)) return null;
            // Return the type prefix as the alert name (e.g. 'EXPOSED_BURNER', 'POT_TOO_HOT', 'UNATTENDED')
            const matchedPrefix = alertPrefixes.find((p) => latestAlert.type?.startsWith(p + '.'));
            if (!matchedPrefix) return null;
            const rawKey = matchedPrefix.split('.').pop().toUpperCase(); // e.g. 'EXPOSED_BURNER'
            const alertKey = rawKey === 'UNATTENDED' ? 'UNATTENDED_COOKING' : rawKey;
            return latestAlert.iseventdetected === true ? alertKey : null;
          })()
        : undefined,
    cookingLastSessionTime:
      sensor.devicetype === SENSORS.SMART_HOME.COOKTOP_SENSOR
        ? (() => {
            const timezoneName = timezone?.split(' ')[0];
            const todayStart = timezoneName
              ? moment.tz(timezoneName).startOf('day')
              : moment().startOf('day');

            const allCookingToday = [...(cooktopTodayEvents || []), ...sensorLatestEvents].filter(
              (e) =>
                e.type?.startsWith(EVENT_TYPES.COOKTOP_SENSOR.ACTIVE_COOKING + '.') &&
                moment(e.utc_timestamp_inms).isSameOrAfter(todayStart)
            );

            // When active: show the time the session started (detected)
            // When inactive: show the time the session ended (cleared)
            const targetEvent = getLatestData(
              allCookingToday.filter((e) =>
                cookingSessionActive ? e.iseventdetected === true : e.iseventdetected === false
              )
            );

            // Fall back to any cooking event today if no cleared/detected found
            const fallbackEvent = isEmpty(targetEvent)
              ? getLatestData(allCookingToday)
              : targetEvent;

            if (isEmpty(fallbackEvent)) return '-';
            return fallbackEvent.utc_timestamp_inms
              ? getTimeFormat(
                  fallbackEvent.utc_timestamp_inms,
                  getTimeZoneFormat(timezone),
                  loginUserTimeformat?.is24hoursformat
                )
              : '-';
          })()
        : undefined,
    ambientLight: getSensorState(
      groupedLatestEvent[EVENT_TYPES.ENVIRONMENTAL_SENSOR.SENSOR_LIGHT_REPORT],
      sensor,
      'ambientLight',
      trans,
      null
    ),
    weatherId: sensor?.weatherId,
    flushedCount: getFlushedCount(aggregatedEvents),
    cookingSessionsCount:
      sensor.devicetype === SENSORS.SMART_HOME.COOKTOP_SENSOR
        ? getCookingSessionsCount()
        : undefined,
    cookingSessionActive,
    pairedStatus: sensor?.paired_status,
    zoneName: lastEventData?.['radar_presence_event__zone_name'] || '-',
    fallStatus: radarFallInfo.status,
    fallEventTime: radarFallInfo.eventTime,
    fallsTodayCount: radarFallsTodayCount,
    currentFallState: radarFallInfo.status,
  };
};
